import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useFaqsRealtime } from "@/lib/supabaseRealtime";

const FALLBACK_QAS = [
  { question: "Can I travel with CryoVault on a plane?", answer: "Yes. CryoVault is designed to travel with you. It is compact enough for any carry-on, and its sealed medical-grade interior keeps your vials at a stable 2–8°C throughout your journey." },
  { question: "Is it TSA approved?", answer: "CryoVault is TSA-friendly. Customers routinely bring it through security in the US and internationally with medical documentation for their peptides or insulin." },
  { question: "How long does the battery last?", answer: "The Original lasts up to 10 hours unplugged per full charge, and the XL lasts up to 14 hours. A full charge takes under 3 hours via USB-C." },
  { question: "What temperature range does it hold?", answer: "CryoVault is locked at a clinical 2–8°C with ±0.5°C accuracy. You can dial in your exact target on the digital display." },
  { question: "Can I store insulin in it?", answer: "Yes. Many of our customers use CryoVault to store insulin at the manufacturer-recommended temperature. We do not, however, provide medical dosing guidance." },
  { question: "Can I store peptides in it?", answer: "Yes. CryoVault was engineered specifically for temperature-sensitive biologics like peptides. The sealed interior also protects against moisture and condensation." },
  { question: "How does it charge?", answer: "CryoVault charges via a standard USB-C cable. Choose your plug region (EU, UK, US) at checkout for the included adapter." },
  { question: "What warranty do I get?", answer: "Every CryoVault ships with a 1-year warranty as standard. Registered customers receive an extended 2-year warranty at no extra cost." },
  { question: "How fast is shipping?", answer: "We ship worldwide. Most orders arrive within 3–7 business days depending on your region. Free shipping is included on every order." },
  { question: "What is your return policy?", answer: "You have 14 days from delivery to return an unused CryoVault for a full refund. Please contact support to initiate a return." },
];

export default function FAQ() {
  const { data: remote, error } = useFaqsRealtime();
  // Use Supabase FAQs when available; fall back to static list to keep UI unchanged.
  const items =
    !error && Array.isArray(remote) && remote.length > 0 ? remote : FALLBACK_QAS;

  return (
    <section id="faq" data-testid="faq" className="scroll-mt-24 md:scroll-mt-28 lg:scroll-mt-32 py-14 sm:py-20 bg-cool-white">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <div className="reveal">
          <div className="overline text-cyan-brand">FAQ</div>
          <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-deep tracking-tight leading-[1.05]">
            Everything you want to know.
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="mt-12 reveal rounded-3xl bg-white border border-[#8A97A8]/25 px-6 sm:px-8"
          data-testid="faq-accordion"
        >
          {items.map((it, i) => (
            <AccordionItem
              key={it.id || i}
              value={`item-${i}`}
              className="border-b border-[#8A97A8]/15 last:border-b-0"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left text-navy-deep hover:no-underline py-6 text-base sm:text-lg font-display font-semibold hover:text-cyan-brand transition-colors"
              >
                {it.question || it.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-[15px] leading-relaxed pb-6 pr-6">
                {it.answer || it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
